import { DEFAULT_GAME_MODE, type GameModeId } from '../content/modes';
import { FIELD_HEIGHT, FIELD_WIDTH } from '../../config/gameplay';
import { getCharacterForPhase, type CharacterId } from '../content/characters';
import type { ResonancePhase } from '../simulation/dropBag';
import { normalizedPartsToWorld, type Point } from '../geometry';

export type AutoBody = Readonly<{
  entityId: string;
  characterId: CharacterId;
  x: number;
  y: number;
  parts: readonly (readonly Point[])[];
}>;

export type DropPlan = Readonly<{
  x: number;
  y: number;
  score: number;
  merges: number;
  targetEntityId: string | null;
}>;

const GRID = 4;
const CELLS = Math.ceil(FIELD_WIDTH / GRID) + 1;
type Surface = { top: number[]; owner: (AutoBody | null)[] };
type Shape = { offsets: { x: number; bottom: number }[]; top: number; bottom: number };
const shapes = new Map<string, Shape>();

function verticalRange(parts: AutoBody['parts'], x: number): [number, number] | null {
  let top = Infinity;
  let bottom = -Infinity;
  for (const points of parts) {
    for (let i = 0; i < points.length; i++) {
      const a = points[i]!;
      const b = points[(i + 1) % points.length]!;
      if (x < Math.min(a.x, b.x) || x > Math.max(a.x, b.x)) continue;
      if (Math.abs(b.x - a.x) < 0.0001) {
        top = Math.min(top, a.y, b.y);
        bottom = Math.max(bottom, a.y, b.y);
      } else {
        const y = a.y + (b.y - a.y) * (x - a.x) / (b.x - a.x);
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
      }
    }
  }
  return Number.isFinite(top) ? [top, bottom] : null;
}

function shape(id: CharacterId, phase: ResonancePhase, mode: GameModeId): Shape {
  const key = `${mode}:${phase}:${id}`;
  const cached = shapes.get(key);
  if (cached) return cached;
  const c = getCharacterForPhase(id, phase, mode);
  const parts = normalizedPartsToWorld(c.collisionParts, c.centerOfMass, c.baseSize, { x: 0, y: 0 }, c.initialAngle);
  const points = parts.flat();
  const offsets: Shape['offsets'] = [];
  for (let x = Math.ceil(Math.min(...points.map(p => p.x)) / GRID) * GRID; x <= Math.max(...points.map(p => p.x)); x += GRID) {
    const range = verticalRange(parts, x);
    if (range) offsets.push({ x, bottom: range[1] });
  }
  const result = { offsets, top: Math.min(...points.map(p => p.y)), bottom: Math.max(...points.map(p => p.y)) };
  shapes.set(key, result);
  return result;
}

function surface(bodies: readonly AutoBody[]): Surface {
  const top = Array<number>(CELLS).fill(FIELD_HEIGHT);
  const owner = Array<AutoBody | null>(CELLS).fill(null);
  for (const body of bodies) {
    const vertices = body.parts.flat();
    if (!vertices.length) continue;
    const left = Math.max(0, Math.ceil(Math.min(...vertices.map(p => p.x)) / GRID));
    const right = Math.min(CELLS - 1, Math.floor(Math.max(...vertices.map(p => p.x)) / GRID));
    for (let i = left; i <= right; i++) {
      const range = verticalRange(body.parts, i * GRID);
      if (range && range[0] < top[i]!) {
        top[i] = range[0];
        owner[i] = body;
      }
    }
  }
  return { top, owner };
}

function candidates(id: CharacterId, bodies: readonly AutoBody[], field: Surface, phase: ResonancePhase, mode: GameModeId): DropPlan[] {
  const c = getCharacterForPhase(id, phase, mode);
  const profile = shape(id, phase, mode);
  const margin = Math.max(26, c.baseSize / 2 + 7);
  const plans: DropPlan[] = [];
  const firstGridX = Math.ceil(margin / GRID) * GRID;
  const lastGridX = FIELD_WIDTH - margin;
  // Tall artwork can have a square texture wider than the board; retain a centered candidate.
  const firstX = firstGridX <= lastGridX ? firstGridX : FIELD_WIDTH / 2;
  const lastX = firstGridX <= lastGridX ? lastGridX : firstX;
  for (let x = firstX; x <= lastX; x += GRID) {
    let y = FIELD_HEIGHT - profile.bottom;
    let target: AutoBody | null = null;
    let contactX = x;
    for (const offset of profile.offsets) {
      const cell = Math.round((x + offset.x) / GRID);
      const contactY = (field.top[cell] ?? FIELD_HEIGHT) - offset.bottom;
      if (contactY < y) {
        y = contactY;
        target = field.owner[cell] ?? null;
        contactX = x + offset.x;
      }
    }
    const merges = target?.characterId === id && c.nextId !== null ? 1 : 0;
    const height = FIELD_HEIGHT - (y + profile.top);
    const aboveWall = Math.max(0, -(y + profile.top));
    const edgeDistance = Math.min(x - margin, FIELD_WIDTH - margin - x);
    // Above the finite walls, a side landing can push the tower out of play.
    let score = merges * (180 + c.stage * 8) - height * 0.24 - aboveWall * 0.9;
    score -= Math.max(0, 65 - edgeDistance) * (aboveWall > 0 ? 2.2 : 0.12);
    score -= Math.abs(x - FIELD_WIDTH / 2) * 0.025;
    if (!merges && target) {
      score -= Math.abs(contactX - x) * 1.2;
      score -= Math.abs(target.x - x) * 0.18;
      // Preserve exposed pairs instead of covering them with an unrelated piece.
      if (bodies.some(b => b !== target && b.characterId === target.characterId)) score -= 22;
    }
    plans.push({ x, y, score, merges, targetEntityId: target?.entityId ?? null });
  }
  return plans.sort((a, b) => b.score - a.score || Math.abs(a.x - 210) - Math.abs(b.x - 210));
}

function project(id: CharacterId, plan: DropPlan, bodies: readonly AutoBody[], phase: ResonancePhase, mode: GameModeId): { bodies: AutoBody[]; chains: number } {
  let character = getCharacterForPhase(id, phase, mode);
  let x = plan.x;
  let y = plan.y;
  let remaining = [...bodies];
  let target = plan.merges ? remaining.find(b => b.entityId === plan.targetEntityId) : undefined;
  let chains = 0;
  while (target && character.nextId && chains < 5) {
    remaining = remaining.filter(b => b.entityId !== target!.entityId);
    x = (x + target.x) / 2;
    y = (y + target.y) / 2;
    character = getCharacterForPhase(character.nextId, phase, mode);
    chains++;
    // Conservative nearby-chain estimate; actual contacts are resolved by Matter.
    target = remaining.find(b => b.characterId === character.id &&
      Math.hypot(b.x - x, b.y - y) < character.baseSize * 0.66);
  }
  remaining.push({
    entityId: 'autoplay-projection', characterId: character.id, x, y,
    parts: normalizedPartsToWorld(character.collisionParts, character.centerOfMass, character.baseSize, { x, y }, character.initialAngle),
  });
  return { bodies: remaining, chains };
}

/** Bounded two-ply search using the visible queue and current collision silhouettes. */
export function planAutoDrop(id: CharacterId, nextId: CharacterId | null, bodies: readonly AutoBody[], phase: ResonancePhase = 1, mode: GameModeId = DEFAULT_GAME_MODE): DropPlan {
  const ranked = candidates(id, bodies, surface(bodies), phase, mode);
  // Keep spatially distinct alternatives so one broad target cannot occupy the beam.
  const beam: DropPlan[] = [];
  for (const plan of ranked) {
    if (beam.every(other => Math.abs(other.x - plan.x) >= 20)) beam.push(plan);
    if (beam.length === 6) break;
  }
  let best = ranked[0]!;
  let bestScore = -Infinity;
  for (const plan of beam) {
    const projected = project(id, plan, bodies, phase, mode);
    const next = nextId ? candidates(nextId, projected.bodies, surface(projected.bodies), phase, mode)[0] : null;
    const score = plan.score + Math.max(0, projected.chains - 1) * 140 + (next?.score ?? 0) * 0.55;
    if (score > bestScore) {
      bestScore = score;
      best = { ...plan, score, merges: projected.chains };
    }
  }
  return best;
}
